import { useApi } from "@appretio/api";
import { ContextSelectedOptions } from "@appretio/context-selector";
import { useQuery } from "@tanstack/react-query";
import { TemporalContext } from "../../../components/temporalContextSelector/data/temporalContextOptions";
import { convertOptionsToApiFormat } from "../utils/convertToApiFormat";
import { categoryAggregatesQueryKeys } from "./queryKeys";

interface UseCategoryAggregatesDataProps {
  contextOptions: ContextSelectedOptions;
  temporalOptions: TemporalContext;
  level: "category" | "subcategory";
  selectedCategory?: string;
  enabled?: boolean;
}

export function useCategoryAggregatesData({
  contextOptions,
  temporalOptions,
  level,
  selectedCategory,
  enabled = false
}: UseCategoryAggregatesDataProps) {
  const apiClient = useApi();

  return useQuery({
    queryKey: categoryAggregatesQueryKeys.data(level, selectedCategory, {
      contextOptions,
      temporalOptions
    }),
    queryFn: async () => {
      const baseOptions = convertOptionsToApiFormat(
        contextOptions,
        temporalOptions
      );

      const query_params = {
        offset: 0,
        group: {
          columns: [
            level === "category" ? "product_category" : "style_subcategory"
          ]
        },
        aggregates: [
          { column: "nis3", function: "sum" as const, alias: "nis3_sum" },
          {
            column: "time_purchase",
            function: "sum" as const,
            alias: "time_purchase_sum"
          },
          { column: "st", function: "avg" as const, alias: "st_avg" }
        ]
      };

      const requestBody = {
        ...baseOptions,
        ...(level === "subcategory" &&
          selectedCategory && {
            style_context: {
              ...baseOptions.style_context,
              product_category: [selectedCategory]
            }
          }),
        query_params
      };

      const response = await apiClient.POST(
        "/v1/in-season/review/get-review-data",
        {
          body: requestBody
        }
      );

      const result = response.data?.result ?? [];
      return result;
    },
    enabled: enabled && (level === "category" || !!selectedCategory),
    staleTime: 5 * 60 * 1000
  });
}
